// ============================================
// 그룹 관리 컴포넌트 (심플 HTML 스타일)
// ============================================
import React, { useState, useEffect } from 'react';
import { db, auth, analytics, logEvent } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';

function Groups() {
  const [myGroups, setMyGroups] = useState([]);
  const [primaryGroupId, setPrimaryGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const [message, setMessage] = useState('');

  // 방명록 관련
  const [guestbookMessages, setGuestbookMessages] = useState([]);
  const [guestbookInput, setGuestbookInput] = useState('');
  const [sendingGuestbook, setSendingGuestbook] = useState(false);

  // 그룹 상세 정보
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupRanking, setGroupRanking] = useState([]);
  const [groupTotalSteps, setGroupTotalSteps] = useState(0);
  const [groupDetailTab, setGroupDetailTab] = useState('info'); // 'info', 'members', 'guestbook'

  useEffect(() => {
    loadMyGroups();
    checkInviteLink();
  }, []);

  const checkInviteLink = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('invite');
    
    if (code) {
      setInviteCode(code);
      setShowJoinModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const loadMyGroups = async () => {
    setLoading(true);
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const allGroups = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const myGroupsList = allGroups.filter(group => 
        group.members && group.members.includes(auth.currentUser.uid)
      );

      setMyGroups(myGroupsList);

      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists() && userDoc.data().primaryGroupId) {
        setPrimaryGroupId(userDoc.data().primaryGroupId);
      }

    } catch (error) {
      console.error('그룹 로드 실패:', error);
    }
    setLoading(false);
  };

  // 대표 그룹 설정
  const handleSetPrimaryGroup = async (groupId) => {
    try {
      await setDoc(doc(db, 'users', auth.currentUser.uid), {
        primaryGroupId: groupId,
        updatedAt: new Date()
      }, { merge: true });

      setPrimaryGroupId(groupId);
      setMessage('대표 그룹이 설정되었습니다.');

      logEvent(analytics, 'set_primary_group', {
        group_id: groupId
      });

      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('설정 실패: ' + error.message);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      setMessage('그룹 이름을 입력해주세요.');
      return;
    }

    setCreating(true);
    setMessage('');

    try {
      const inviteCode = generateInviteCode();

      const groupRef = await addDoc(collection(db, 'groups'), {
        name: groupName,
        description: groupDescription,
        inviteCode: inviteCode,
        ownerId: auth.currentUser.uid,
        ownerEmail: auth.currentUser.email,
        members: [auth.currentUser.uid],
        createdAt: new Date(),
        updatedAt: new Date()
      });

      if (myGroups.length === 0) {
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          primaryGroupId: groupRef.id,
          updatedAt: new Date()
        }, { merge: true });
        setPrimaryGroupId(groupRef.id);
      }

      setMessage('그룹이 생성되었습니다.');
      setGroupName('');
      setGroupDescription('');
      setShowCreateModal(false);

      logEvent(analytics, 'create_group', {
        group_id: groupRef.id,
        is_first_group: myGroups.length === 0
      });

      await loadMyGroups();

    } catch (error) {
      setMessage('그룹 생성 실패: ' + error.message);
      console.error(error);
    }
    setCreating(false);
  };

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      setMessage('초대 코드를 입력해주세요.');
      return;
    }

    setJoining(true);
    setMessage('');

    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const foundGroup = groupsSnapshot.docs.find(doc => 
        doc.data().inviteCode === inviteCode.toUpperCase()
      );

      if (!foundGroup) {
        setMessage('유효하지 않은 초대 코드입니다.');
        setJoining(false);
        return;
      }

      const groupData = foundGroup.data();

      if (groupData.members && groupData.members.includes(auth.currentUser.uid)) {
        setMessage('이미 가입된 그룹입니다.');
        setJoining(false);
        return;
      }

      await updateDoc(doc(db, 'groups', foundGroup.id), {
        members: arrayUnion(auth.currentUser.uid),
        updatedAt: new Date()
      });

      if (myGroups.length === 0) {
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          primaryGroupId: foundGroup.id,
          updatedAt: new Date()
        }, { merge: true });
        setPrimaryGroupId(foundGroup.id);
      }

      setMessage('그룹에 가입되었습니다.');
      setInviteCode('');
      setShowJoinModal(false);

      logEvent(analytics, 'join_group', {
        group_id: foundGroup.id,
        is_first_group: myGroups.length === 0
      });

      await loadMyGroups();

    } catch (error) {
      setMessage('가입 실패: ' + error.message);
      console.error(error);
    }
    setJoining(false);
  };

  const handleLeaveGroup = async (groupId, isOwner) => {
    if (isOwner) {
      const choice = window.confirm(
        '그룹 소유자입니다.\n\n' +
        '확인: 그룹 완전 삭제 (모든 멤버 제거)\n' +
        '취소: 취소'
      );
      
      if (!choice) return;

      try {
        await deleteDoc(doc(db, 'groups', groupId));
        
        if (primaryGroupId === groupId) {
          await setDoc(doc(db, 'users', auth.currentUser.uid), {
            primaryGroupId: null,
            updatedAt: new Date()
          }, { merge: true });
          setPrimaryGroupId(null);
        }

        setMessage('그룹이 삭제되었습니다.');

        logEvent(analytics, 'delete_group', {
          group_id: groupId
        });

        await loadMyGroups();
      } catch (error) {
        setMessage('삭제 실패: ' + error.message);
      }
      return;
    }

    if (!window.confirm('정말 이 그룹을 나가시겠습니까?')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'groups', groupId), {
        members: arrayRemove(auth.currentUser.uid),
        updatedAt: new Date()
      });

      if (primaryGroupId === groupId) {
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          primaryGroupId: null,
          updatedAt: new Date()
        }, { merge: true });
        setPrimaryGroupId(null);
      }

      setMessage('그룹에서 나갔습니다.');

      logEvent(analytics, 'leave_group', {
        group_id: groupId
      });

      await loadMyGroups();
    } catch (error) {
      setMessage('나가기 실패: ' + error.message);
    }
  };

  const handleViewGroup = async (group) => {
    setSelectedGroup(group);
    setGroupDetailTab('info');
    await Promise.all([
      loadGuestbook(group.id),
      loadGroupMembers(group.id),
      loadGroupRanking(group.id)
    ]);
  };

  const loadGroupMembers = async (groupId) => {
    try {
      const groupDoc = await getDoc(doc(db, 'groups', groupId));
      if (!groupDoc.exists()) return;

      const memberIds = groupDoc.data().members || [];
      const memberPromises = memberIds.map(async (memberId) => {
        const userDoc = await getDoc(doc(db, 'users', memberId));
        if (userDoc.exists()) {
          return {
            id: memberId,
            ...userDoc.data()
          };
        }
        return null;
      });

      const members = (await Promise.all(memberPromises)).filter(m => m !== null);
      setGroupMembers(members);
    } catch (error) {
      console.error('멤버 로드 실패:', error);
    }
  };

  const loadGroupRanking = async (groupId) => {
    try {
      const groupDoc = await getDoc(doc(db, 'groups', groupId));
      if (!groupDoc.exists()) return;

      const memberIds = groupDoc.data().members || [];

      // 각 멤버의 계단 기록 가져오기
      const rankingPromises = memberIds.map(async (memberId) => {
        const userDoc = await getDoc(doc(db, 'users', memberId));
        const recordsSnapshot = await getDocs(
          query(
            collection(db, 'user_records'),
            where('userId', '==', memberId)
          )
        );

        const totalSteps = recordsSnapshot.docs.reduce((sum, doc) => {
          return sum + (doc.data().steps || 0);
        }, 0);

        return {
          userId: memberId,
          nickname: userDoc.exists() ? userDoc.data().nickname : '알 수 없음',
          photoURL: userDoc.exists() ? userDoc.data().photoURL : null,
          totalSteps: totalSteps,
          recordCount: recordsSnapshot.size
        };
      });

      const ranking = await Promise.all(rankingPromises);
      ranking.sort((a, b) => b.totalSteps - a.totalSteps);

      // 그룹 전체 누적 계단 수 계산
      const totalSteps = ranking.reduce((sum, member) => sum + member.totalSteps, 0);
      setGroupTotalSteps(totalSteps);
      setGroupRanking(ranking);
    } catch (error) {
      console.error('랭킹 로드 실패:', error);
    }
  };

  const loadGuestbook = async (groupId) => {
    try {
      const guestbookRef = collection(db, 'groups', groupId, 'guestbook');
      const q = query(guestbookRef, orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);

      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setGuestbookMessages(messages);
    } catch (error) {
      console.error('방명록 로드 실패:', error);
    }
  };

  const handleSendGuestbook = async (e) => {
    e.preventDefault();

    if (!guestbookInput.trim()) {
      return;
    }

    if (!selectedGroup) {
      return;
    }

    setSendingGuestbook(true);

    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      const nickname = userDoc.exists() ? userDoc.data().nickname : '익명';

      const guestbookRef = collection(db, 'groups', selectedGroup.id, 'guestbook');
      await addDoc(guestbookRef, {
        userId: auth.currentUser.uid,
        nickname: nickname,
        message: guestbookInput.trim(),
        createdAt: new Date()
      });

      setGuestbookInput('');
      await loadGuestbook(selectedGroup.id);

      logEvent(analytics, 'send_guestbook_message', {
        group_id: selectedGroup.id
      });
    } catch (error) {
      console.error('방명록 전송 실패:', error);
      setMessage('메시지 전송에 실패했습니다.');
    }

    setSendingGuestbook(false);
  };

  const handleViewRanking = (groupId) => {
    const event = new CustomEvent('changePage', { 
      detail: 'ranking',
      groupId: groupId
    });
    window.dispatchEvent(event);
  };

  const getInviteLink = (inviteCode) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}?invite=${inviteCode}`;
  };

  const handleCopyLink = async (inviteCode) => {
    const inviteLink = getInviteLink(inviteCode);

    logEvent(analytics, 'copy_group_invite_link', {
      invite_code: inviteCode
    });

    try {
      await navigator.clipboard.writeText(inviteLink);
      setMessage('초대 링크가 복사되었습니다.');
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = inviteLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setMessage('초대 링크가 복사되었습니다.');
    }

    setTimeout(() => {
      if (message.includes('복사')) {
        setMessage('');
      }
    }, 3000);
  };

  const handleShareKakao = (group) => {
    const inviteLink = getInviteLink(group.inviteCode);
    const shareText = `${group.name} 그룹에 초대합니다.\n\n${inviteLink}`;
    
    if (navigator.share) {
      navigator.share({
        title: `${group.name} 초대`,
        text: shareText,
        url: inviteLink
      }).catch(err => console.log('공유 취소:', err));
    } else {
      handleCopyLink(group.inviteCode);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>그룹 관리</h2>
        <div style={styles.loading}>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>그룹 관리</h2>

      {message && (
        <div style={styles.message}>{message}</div>
      )}

      <div style={styles.buttonRow}>
        <button 
          onClick={() => setShowCreateModal(true)}
          style={styles.buttonPrimary}
        >
          그룹 만들기
        </button>
        <button 
          onClick={() => setShowJoinModal(true)}
          style={styles.buttonDefault}
        >
          그룹 가입하기
        </button>
      </div>

      {myGroups.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyMain}>가입한 그룹이 없습니다.</p>
          <p style={styles.emptySub}>그룹을 만들거나 초대 코드로 가입해보세요.</p>
        </div>
      ) : (
        <div style={styles.groupList}>
          {myGroups.map(group => {
            const isOwner = group.ownerId === auth.currentUser.uid;
            const isPrimary = group.id === primaryGroupId;
            const memberCount = group.members ? group.members.length : 0;

            return (
              <div
                key={group.id}
                style={{
                  ...styles.groupItem,
                  ...(isPrimary ? styles.groupItemPrimary : {}),
                  cursor: 'pointer'
                }}
                onClick={() => handleViewGroup(group)}
              >
                <div style={styles.groupHeader}>
                  <div style={styles.groupTitle}>
                    {group.name}
                    {isOwner && <span style={styles.chipOwner}>소유자</span>}
                    {isPrimary && <span style={styles.chipPrimary}>대표 그룹</span>}
                  </div>
                  <div style={styles.groupMeta}>
                    멤버 {memberCount}명
                  </div>
                </div>

                {group.description && (
                  <div style={styles.groupDesc}>{group.description}</div>
                )}

                <div style={styles.groupActionsRow} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleCopyLink(group.inviteCode)}
                    style={styles.smallButton}
                  >
                    링크 복사
                  </button>
                  {!isPrimary && (
                    <button
                      onClick={() => handleSetPrimaryGroup(group.id)}
                      style={styles.smallButton}
                    >
                      대표로 설정
                    </button>
                  )}
                  <button
                    onClick={() => handleLeaveGroup(group.id, isOwner)}
                    style={styles.smallButtonDanger}
                  >
                    {isOwner ? '그룹 삭제' : '그룹 나가기'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 그룹 생성 모달 */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>새 그룹 만들기</h3>
            <form onSubmit={handleCreateGroup}>
              <div style={styles.field}>
                <label style={styles.label}>그룹 이름 *</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="예: 회사 러닝 계단 모임"
                  style={styles.input}
                  required
                  maxLength={30}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>그룹 설명 (선택)</label>
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="그룹을 간단히 설명해 주세요."
                  style={styles.textarea}
                  maxLength={100}
                />
              </div>

              <div style={styles.modalButtonRow}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={styles.buttonDefault}
                >
                  닫기
                </button>
                <button
                  type="submit"
                  style={styles.buttonPrimary}
                  disabled={creating}
                >
                  {creating ? '생성 중...' : '생성하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 그룹 가입 모달 */}
      {showJoinModal && (
        <div style={styles.modalOverlay} onClick={() => setShowJoinModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>그룹 가입하기</h3>
            <form onSubmit={handleJoinGroup}>
              <div style={styles.field}>
                <label style={styles.label}>초대 코드 *</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  style={{ ...styles.input, textTransform: 'uppercase' }}
                  required
                  maxLength={6}
                />
                <p style={styles.hint}>
                  그룹 소유자에게 받은 6자리 코드를 입력하세요.
                </p>
              </div>

              <div style={styles.modalButtonRow}>
                <button
                  type="button"
                  onClick={() => setShowJoinModal(false)}
                  style={styles.buttonDefault}
                >
                  닫기
                </button>
                <button
                  type="submit"
                  style={styles.buttonPrimary}
                  disabled={joining}
                >
                  {joining ? '가입 중...' : '가입하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 그룹 상세 모달 */}
      {selectedGroup && (
        <div style={styles.modalOverlay} onClick={() => setSelectedGroup(null)}>
          <div style={styles.modalLarge} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{selectedGroup.name}</h3>

            {/* 탭 버튼 */}
            <div style={styles.tabBar}>
              <button
                onClick={() => setGroupDetailTab('info')}
                style={{
                  ...styles.tabButton,
                  ...(groupDetailTab === 'info' ? styles.tabButtonActive : {})
                }}
              >
                정보
              </button>
              <button
                onClick={() => setGroupDetailTab('members')}
                style={{
                  ...styles.tabButton,
                  ...(groupDetailTab === 'members' ? styles.tabButtonActive : {})
                }}
              >
                멤버·랭킹
              </button>
              <button
                onClick={() => setGroupDetailTab('guestbook')}
                style={{
                  ...styles.tabButton,
                  ...(groupDetailTab === 'guestbook' ? styles.tabButtonActive : {})
                }}
              >
                방명록
              </button>
            </div>

            {/* 정보 탭 */}
            {groupDetailTab === 'info' && (
              <div style={styles.tabContent}>
                <div style={styles.infoSection}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>그룹 누적 계단:</span>
                    <span style={styles.infoValue}>{groupTotalSteps.toLocaleString()}개</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>멤버 수:</span>
                    <span style={styles.infoValue}>{selectedGroup.members?.length || 0}명</span>
                  </div>
                  {selectedGroup.description && (
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>설명:</span>
                      <span style={styles.infoValue}>{selectedGroup.description}</span>
                    </div>
                  )}
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>초대 코드:</span>
                    <span style={styles.infoValue}><strong>{selectedGroup.inviteCode}</strong></span>
                  </div>
                </div>

                <div style={styles.linkSection}>
                  <div style={styles.linkLabel}>초대 링크:</div>
                  <div style={styles.detailLinkBox}>
                    {getInviteLink(selectedGroup.inviteCode)}
                  </div>
                  <div style={styles.linkButtonRow}>
                    <button
                      onClick={() => handleCopyLink(selectedGroup.inviteCode)}
                      style={styles.buttonPrimary}
                    >
                      링크 복사
                    </button>
                    <button
                      onClick={() => handleShareKakao(selectedGroup)}
                      style={styles.buttonDefault}
                    >
                      링크 공유
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 멤버·랭킹 탭 */}
            {groupDetailTab === 'members' && (
              <div style={styles.tabContent}>
                <div style={styles.rankingHeader}>
                  <h4 style={styles.rankingTitle}>그룹 랭킹</h4>
                  <div style={styles.rankingSubtitle}>
                    그룹 누적: {groupTotalSteps.toLocaleString()}개
                  </div>
                </div>

                {groupRanking.length === 0 ? (
                  <div style={styles.emptyRanking}>멤버 정보를 불러오는 중...</div>
                ) : (
                  <div style={styles.rankingList}>
                    {groupRanking.map((member, index) => (
                      <div key={member.userId} style={styles.rankingItem}>
                        <div style={styles.rankingRank}>
                          {index + 1}
                        </div>
                        <div style={styles.rankingProfile}>
                          {member.photoURL ? (
                            <img
                              src={member.photoURL}
                              alt={member.nickname}
                              style={styles.rankingPhoto}
                            />
                          ) : (
                            <div style={styles.rankingPhotoPlaceholder}>
                              {member.nickname.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div style={styles.rankingInfo}>
                          <div style={styles.rankingNickname}>{member.nickname}</div>
                          <div style={styles.rankingStats}>
                            {member.totalSteps.toLocaleString()}개 · {member.recordCount}회
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 방명록 탭 */}
            {groupDetailTab === 'guestbook' && (
              <div style={styles.tabContent}>
                {/* 방명록 작성 폼 */}
                <form onSubmit={handleSendGuestbook} style={styles.guestbookForm}>
                  <textarea
                    value={guestbookInput}
                    onChange={(e) => setGuestbookInput(e.target.value)}
                    placeholder="그룹 멤버들에게 메시지를 남겨보세요..."
                    style={styles.guestbookTextarea}
                    maxLength={200}
                    disabled={sendingGuestbook}
                  />
                  <div style={styles.guestbookFormFooter}>
                    <span style={styles.charCount}>{guestbookInput.length} / 200</span>
                    <button
                      type="submit"
                      style={styles.guestbookSendButton}
                      disabled={sendingGuestbook || !guestbookInput.trim()}
                    >
                      {sendingGuestbook ? '전송 중...' : '작성'}
                    </button>
                  </div>
                </form>

                {/* 방명록 메시지 목록 */}
                <div style={styles.guestbookList}>
                  {guestbookMessages.length === 0 ? (
                    <div style={styles.guestbookEmpty}>
                      아직 작성된 메시지가 없습니다.
                    </div>
                  ) : (
                    guestbookMessages.map((msg) => (
                      <div key={msg.id} style={styles.guestbookItem}>
                        <div style={styles.guestbookHeader}>
                          <span style={styles.guestbookNickname}>{msg.nickname}</span>
                          <span style={styles.guestbookTime}>
                            {msg.createdAt?.toDate?.().toLocaleString('ko-KR', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div style={styles.guestbookMessage}>{msg.message}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div style={styles.modalButtonRow}>
              <button
                onClick={() => setSelectedGroup(null)}
                style={styles.buttonDefault}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 스타일 (심플 HTML 스타일)
// ============================================
const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '16px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  loading: {
    padding: '24px 0',
    textAlign: 'center',
    color: '#666',
    fontSize: '14px'
  },
  message: {
    padding: '10px 12px',
    marginBottom: '16px',
    border: '1px solid #c8e6c9',
    backgroundColor: '#f1f8e9',
    fontSize: '13px'
  },
  buttonRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  },
  buttonPrimary: {
    flex: 1,
    padding: '10px 0',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  buttonDefault: {
    flex: 1,
    padding: '10px 0',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '13px',
    cursor: 'pointer'
  },
  empty: {
    padding: '24px 0',
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    textAlign: 'center'
  },
  emptyMain: {
    margin: 0,
    fontSize: '14px',
    color: '#333'
  },
  emptySub: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#777'
  },
  groupList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  groupItem: {
    border: '1px solid #ddd',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '13px'
  },
  groupItemPrimary: {
    borderColor: '#000'
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  },
  groupTitle: {
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  groupMeta: {
    fontSize: '12px',
    color: '#666'
  },
  chipOwner: {
    fontSize: '11px',
    border: '1px solid #ccc',
    padding: '2px 6px'
  },
  chipPrimary: {
    fontSize: '11px',
    border: '1px solid #000',
    padding: '2px 6px'
  },
  groupDesc: {
    marginTop: '4px',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#555'
  },
  groupMetaBox: {
    padding: '8px',
    backgroundColor: '#f7f7f7',
    fontSize: '12px',
    marginTop: '4px',
    marginBottom: '6px'
  },
  linkText: {
    marginTop: '4px',
    wordBreak: 'break-all',
    color: '#1a73e8'
  },
  groupActionsRow: {
    display: 'flex',
    gap: '6px',
    marginTop: '6px'
  },
  smallButton: {
    flex: 1,
    padding: '8px 0',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    fontSize: '12px',
    cursor: 'pointer'
  },
  smallButtonDanger: {
    flex: 1,
    padding: '8px 0',
    border: '1px solid #c62828',
    backgroundColor: '#fff',
    color: '#c62828',
    fontSize: '12px',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    padding: '16px',
    maxWidth: '420px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    fontSize: '13px'
  },
  modalLarge: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    padding: '16px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    fontSize: '13px'
  },
  modalTitle: {
    marginTop: 0,
    marginBottom: '12px',
    fontSize: '16px',
    fontWeight: '600'
  },
  field: {
    marginBottom: '12px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '13px',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    borderRadius: '2px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    borderRadius: '2px',
    boxSizing: 'border-box',
    minHeight: '70px',
    resize: 'vertical'
  },
  hint: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#777'
  },
  modalButtonRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px'
  },
  detailBox: {
    padding: '8px 0',
    fontSize: '13px'
  },
  detailLinkBox: {
    marginTop: '4px',
    padding: '6px',
    backgroundColor: '#f7f7f7',
    fontSize: '12px',
    wordBreak: 'break-all'
  },
  guestbookSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #ddd'
  },
  guestbookTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600'
  },
  guestbookForm: {
    marginBottom: '12px'
  },
  guestbookTextarea: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    borderRadius: '2px',
    boxSizing: 'border-box',
    minHeight: '60px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  guestbookFormFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px'
  },
  charCount: {
    fontSize: '11px',
    color: '#777'
  },
  guestbookSendButton: {
    padding: '6px 16px',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer'
  },
  guestbookList: {
    maxHeight: '300px',
    overflow: 'auto',
    border: '1px solid #ddd',
    borderRadius: '2px'
  },
  guestbookEmpty: {
    padding: '16px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#777'
  },
  guestbookItem: {
    padding: '10px',
    borderBottom: '1px solid #eee'
  },
  guestbookHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  },
  guestbookNickname: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333'
  },
  guestbookTime: {
    fontSize: '11px',
    color: '#999'
  },
  guestbookMessage: {
    fontSize: '13px',
    color: '#333',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  tabBar: {
    display: 'flex',
    gap: '6px',
    marginBottom: '16px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  tabButton: {
    flex: 1,
    padding: '8px 0',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#666'
  },
  tabButtonActive: {
    borderBottomColor: '#000',
    fontWeight: '600',
    color: '#000'
  },
  tabContent: {
    minHeight: '300px'
  },
  infoSection: {
    marginBottom: '16px'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    fontSize: '13px'
  },
  infoLabel: {
    color: '#666',
    fontWeight: '500'
  },
  infoValue: {
    color: '#333'
  },
  linkSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #ddd'
  },
  linkLabel: {
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '6px',
    color: '#666'
  },
  linkButtonRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '10px'
  },
  rankingHeader: {
    marginBottom: '16px'
  },
  rankingTitle: {
    margin: '0 0 6px 0',
    fontSize: '14px',
    fontWeight: '600'
  },
  rankingSubtitle: {
    fontSize: '12px',
    color: '#666'
  },
  emptyRanking: {
    padding: '24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#777'
  },
  rankingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  rankingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px',
    border: '1px solid #eee',
    backgroundColor: '#fafafa'
  },
  rankingRank: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '600'
  },
  rankingProfile: {
    width: '36px',
    height: '36px',
    flexShrink: 0
  },
  rankingPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    border: '1px solid #ddd'
  },
  rankingPhotoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eee',
    border: '1px solid #ddd',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666'
  },
  rankingInfo: {
    flex: 1,
    minWidth: 0
  },
  rankingNickname: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '2px'
  },
  rankingStats: {
    fontSize: '12px',
    color: '#666'
  }
};

export default Groups;
